import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function PlanPage() {
  const { t } = useTranslation();
  const { session, refresh, hasPermission } = useAuth();
  const [state, setState] = useState({ current: session.tenant.plan_code, available_plans: [], features: [] });
  const [saving, setSaving] = useState("");
  const canChange = hasPermission("tenant.change_plan");

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/plan");
      setState(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); }, [session?.tenant?.id]);

  const choose = async (code) => {
    if (code === state.current) return;
    // Route through Stripe Checkout so the change is a REAL paid subscription
    // (billing.checkout returns a hosted URL). The direct /tenant/plan POST
    // stays as an emergency staff override, not a customer path.
    setSaving(code);
    try {
      const r = await api.post("/billing/checkout", {
        lookup_key: `${code}_monthly`,
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.assign(r.data.checkout_url);
        return;
      }
      toast.error("URL Stripe introuvable");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving("");
    }
  };

  const openPortal = async () => {
    try {
      const r = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      if (r.data?.portal_url) window.location.assign(r.data.portal_url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="plan-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("plan.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("plan.subtitle")}</p>
      </div>

      <Card className="border-slate-200 bg-white p-5 rounded-xl flex items-center justify-between"
            data-testid="current-plan-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
              {t("plan.current")}
            </div>
            <div className="text-xl font-semibold text-slate-900">
              {state.current}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500">{state.features.length} fonctionnalités actives</div>
          <Button variant="outline" size="sm" onClick={openPortal}
                  data-testid="manage-subscription-btn">
            Gérer l&apos;abonnement
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {state.available_plans.map((p) => {
          const isCurrent = p.code === state.current;
          return (
            <Card
              key={p.code}
              className={cn(
                "p-5 rounded-xl border bg-white flex flex-col transition-all",
                isCurrent ? "border-blue-600 ring-2 ring-blue-500/20" : "border-slate-200 hover:shadow-sm"
              )}
              data-testid={`plan-card-${p.code}`}
            >
              <div className="flex items-baseline justify-between">
                <div className="text-lg font-semibold text-slate-900"
                     style={{ fontFamily: "Manrope, sans-serif" }}>
                  {p.label}
                </div>
                {isCurrent && (
                  <Badge className="bg-blue-600 hover:bg-blue-600 text-white">
                    {t("plan.currentBadge")}
                  </Badge>
                )}
              </div>
              <div className="mt-3">
                <span className="text-3xl font-semibold text-slate-900"
                      style={{ fontFamily: "Manrope, sans-serif" }}>
                  {p.price_eur}
                </span>
                <span className="text-sm text-slate-500 ml-1">{t("plan.priceSuffix")}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500 min-h-[36px]">{p.description}</p>

              <div className="mt-4 text-[10px] font-medium uppercase tracking-widest text-slate-500">
                {t("plan.includedFeatures")}
              </div>
              <ul className="mt-2 space-y-1.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="text-xs text-slate-700 flex items-center gap-2">
                    <Check className="h-3 w-3 text-blue-600" /> {f}
                  </li>
                ))}
              </ul>

              {canChange && !isCurrent && (
                <Button
                  onClick={() => choose(p.code)}
                  disabled={!!saving}
                  data-testid={`choose-plan-${p.code}`}
                  className="mt-5 w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {saving === p.code ? t("common.loading") : t("plan.chooseThisPlan")}
                </Button>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
